/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Select from "react-select";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import Flatpickr from "react-flatpickr";
import { Row, Col, Button, Input, Card, CardBody, Label } from "reactstrap";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { reloadWindowToPath } from "../../Helpers/Utils";

dayjs.extend(isBetween);

const EditManualInvoice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loading = useSelector(s => s?.loading?.loading);

    const [customers, setCustomers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);

    const [form, setForm] = useState({
        customer_id: null,
        vessel_name: "",
        gst_percent: 0,
    });

    const [items, setItems] = useState([]);

    // ⭐ 1. MASTER DATA LOAD
    useEffect(() => {
        const loadMasterData = async () => {
            dispatch(loadingTrue());
            const [cusRes, vehRes, driRes] = await Promise.all([
                Handler({ method: "get", url: "/customer/list.php" }),
                Handler({ method: "get", url: "/vehicle/list.php" }),
                Handler({ method: "get", url: "/driver/list.php" })
            ]);

            if (cusRes?.status || cusRes?.success) setCustomers(cusRes?.data?.map(c => ({ value: c?.id, label: c?.company_name })) || []);
            if (vehRes?.status || vehRes?.success) setVehicles(vehRes?.data?.map(v => ({ value: v?.id, lorry_name: v?.lorry_type, label: `${v?.vehicle_number} (${v?.lorry_type})` })) || []);
            if (driRes?.status || driRes?.success) setDrivers(driRes?.data?.map(d => ({ value: d?.id, label: d?.name })) || []);
            dispatch(loadingFalse());
        };
        loadMasterData();
    }, [dispatch]);

    // ⭐ 2. FETCH INVOICE & MAP DATA
    useEffect(() => {
        const fetchInvoiceDetails = async () => {
            if (!id || vehicles?.length === 0 || drivers?.length === 0) return;

            dispatch(loadingTrue());
            const res = await Handler({ method: "get", url: `/invoice/view_new.php?invoice_id=${id}` });

            if (res?.status || res?.success) {
                const inv = res?.invoice || res?.data?.invoice;

                setForm({
                    customer_id: inv?.customer_id,
                    vessel_name: inv?.vessel_name || "",
                    gst_percent: parseFloat(inv?.gst_percent || 0),
                });

                const rateRes = await Handler({ method: "get", url: `/quotation/get_rate.php?customer_id=${inv?.customer_id}` });

                const mappedItems = (res?.items || res?.data?.items)?.map(it => {
                    const vehicleObj = vehicles?.find(v => v?.lorry_name === it?.lorry_type);
                    const driverObj = drivers?.find(d => String(d?.value) === String(it?.driver_id));
                    const ratesObj = rateRes?.success ? rateRes?.data?.find(r => String(r?.vehicle_id) === String(vehicleObj?.value)) : null;

                    return {
                        id: it?.id,
                        vehicle_id: vehicleObj || null,
                        driver_id: driverObj || null,
                        job_date: dayjs(it?.job_date),
                        work_start_datetime: it?.work_start_datetime ? new Date(it?.work_start_datetime) : null,
                        work_end_datetime: it?.work_end_datetime ? new Date(it?.work_end_datetime) : null,
                        pickup_location: it?.pickup_location || "",
                        delivery_location: it?.delivery_location || "",
                        pallet_per_lorry: it?.pallet_per_lorry || "",
                        quantity: it?.quantity || 1,
                        lorry_type: it?.lorry_type,
                        rates: ratesObj,
                        is_holiday: parseFloat(it?.holiday || 0) > 0,
                        extra_charges: it?.extra_charges || 0, // ⭐ Added Mapping
                        extra_charges_description: it?.extra_charges_description || "", // ⭐ Added Mapping
                        normal_hrs: it?.office_hrs || "0.00",
                        normal_hrs_ot: it?.office_hrs_ot || "0.00",
                        after_office_hrs: it?.after_office_hrs || "0.00",
                        offz_hrs_ot: it?.after_office_hrs_ot || "0.00",
                        holiday: it?.holiday || "0.00",
                        holiday_ot: it?.holiday_ot || "0.00",
                        line_total: it?.line_total || "0.00",
                        rate_applied: it?.rate || 0
                    };
                });
                setItems(mappedItems || []);
            }
            dispatch(loadingFalse());
        };
        fetchInvoiceDetails();
    }, [id, vehicles, drivers, dispatch]);

    // ⭐ 3. UPDATED AUTO CALCULATION LOGIC
    const autoCalculate = (item) => {
        if (!item.work_start_datetime || !item.work_end_datetime || !item.rates) return item;

        const start = dayjs(item.work_start_datetime);
        const end = dayjs(item.work_end_datetime);
        const totalDurationHrs = end.diff(start, 'hour', true);

        if (totalDurationHrs <= 0) return item;

        const dayOfWeek = start.day();
        const startTimeStr = start.format("HH:mm");
        const r = item.rates;

        let normal_hrs = 0, normal_hrs_ot = 0;
        let after_office_hrs = 0, offz_hrs_ot = 0;
        let holiday = 0, holiday_ot = 0;
        let appliedRate = 0;
        let basic_fixed_cost = 0; // ⭐ Added for fixed pricing

        // 1. Logic for Holiday/Sunday
        if (item.is_holiday || dayOfWeek === 0) {
            holiday = Math.min(totalDurationHrs, 3);
            holiday_ot = Math.max(0, totalDurationHrs - 3);
            appliedRate = Number(r.holiday || 0);
            // ⭐ If even 0.1 hour worked, take full holiday basic rate
            basic_fixed_cost = holiday > 0 ? Number(r.holiday || 0) : 0;
        }
        // 2. Logic for Normal/After Office
        else {
            let isNormalWindow = false;
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                if (startTimeStr >= "08:00" && startTimeStr <= "16:59") isNormalWindow = true;
            } else if (dayOfWeek === 6) {
                if (startTimeStr >= "08:00" && startTimeStr <= "11:59") isNormalWindow = true;
            }

            const otHrs = Math.max(0, totalDurationHrs - 3);

            if (isNormalWindow) {
                normal_hrs = Math.min(totalDurationHrs, 3);
                normal_hrs_ot = otHrs;
                appliedRate = Number(r.normal_hrs || 0);
                // ⭐ 1hr or 2hr worked, still basic fixed rate
                basic_fixed_cost = normal_hrs > 0 ? Number(r.normal_hrs || 0) : 0;
            } else {
                after_office_hrs = Math.min(totalDurationHrs, 3);
                offz_hrs_ot = otHrs;
                appliedRate = Number(r.after_office_hrs || 0);
                // ⭐ 1hr or 2hr worked, still basic fixed rate
                basic_fixed_cost = after_office_hrs > 0 ? Number(r.after_office_hrs || 0) : 0;
            }
        }

        // ⭐ FINAL CALCULATION: Fixed Basic + (OT hours * OT Rate) + Extra
        const line_total =
            basic_fixed_cost +
            (normal_hrs_ot * Number(r.normal_hrs_ot || 0)) +
            (offz_hrs_ot * Number(r.offz_hrs_ot || 0)) +
            (holiday_ot * Number(r.holiday_ot || 0)) +
            Number(item.extra_charges || 0);

        return {
            ...item,
            normal_hrs: normal_hrs.toFixed(2),
            normal_hrs_ot: normal_hrs_ot.toFixed(2),
            after_office_hrs: after_office_hrs.toFixed(2),
            offz_hrs_ot: offz_hrs_ot.toFixed(2),
            holiday: holiday.toFixed(2),
            holiday_ot: holiday_ot.toFixed(2),
            line_total: line_total.toFixed(2),
            rate_applied: appliedRate
        };
    };

    const handleItemChange = async (index, field, value) => {
        let updatedItems = [...items];
        updatedItems[index][field] = value;

        if (field === "vehicle_id") {
            const res = await Handler({ method: "get", url: `/quotation/get_rate.php?customer_id=${form?.customer_id}` });
            if (res?.success) {
                updatedItems[index].rates = res?.data?.find(v => String(v?.vehicle_id) === String(value?.value));
                updatedItems[index].lorry_type = value?.lorry_name;
            }
        }

        updatedItems[index] = autoCalculate(updatedItems[index]);
        setItems(updatedItems);
    };

    const subtotal = items?.reduce((acc, it) => acc + Number(it?.line_total || 0), 0);
    const gstAmount = (subtotal * Number(form?.gst_percent || 0)) / 100;
    const grandTotal = subtotal + gstAmount;

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch(loadingTrue());
        const payload = {
            invoice_id: id,
            customer_id: form?.customer_id,
            vessel_name: form?.vessel_name,
            gst_percent: form?.gst_percent,
            items: items?.map(it => ({
                id: it?.id || null,
                lorry_type: it?.lorry_type,
                driver_id: it?.driver_id?.value,
                quantity: it?.quantity,
                pallet_per_lorry: it?.pallet_per_lorry,
                pickup_location: it?.pickup_location,
                delivery_location: it?.delivery_location,
                job_date: dayjs(it?.job_date)?.format("YYYY-MM-DD"),
                rate: it?.rate_applied,
                normal_hrs: it?.normal_hrs,
                normal_hrs_ot: it?.normal_hrs_ot,
                after_office_hrs: it?.after_office_hrs,
                offz_hrs_ot: it?.offz_hrs_ot,
                holiday: it?.holiday,
                holiday_ot: it?.holiday_ot,
                extra_charges: it?.extra_charges, // ⭐ Added to Submit
                extra_charges_description: it?.extra_charges_description, // ⭐ Added to Submit
                line_total: it?.line_total,
                work_start_datetime: it?.work_start_datetime ? dayjs(it?.work_start_datetime)?.format("YYYY-MM-DD HH:mm:ss") : null,
                work_end_datetime: it?.work_end_datetime ? dayjs(it?.work_end_datetime)?.format("YYYY-MM-DD HH:mm:ss") : null,
            }))
        };

        const res = await Handler({ method: "post", url: "/invoice/update.php", data: payload });
        if (res?.status || res?.success) {
            toast.success("Invoice Updated Successfully!");
            reloadWindowToPath('/invoices')
        } else {
            toast.error(res?.message || "Update failed");
        }
        dispatch(loadingFalse());
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-card p-4 bg-white rounded-4 shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                    <h4 className="fw-bold text-primary">Edit Manual Invoice: {id}</h4>
                    <Button color="secondary" outline size="sm" onClick={() => navigate(-1)}>Cancel</Button>
                </div>

                <form onSubmit={handleSubmit}>
                    <Row className="mb-4 g-3">
                        <Col md={4}>
                            <label className="small fw-bold text-muted uppercase">Customer</label>
                            <Select options={customers} value={customers?.find(c => c?.value === form?.customer_id)} isDisabled />
                        </Col>
                        <Col md={4}>
                            <label className="small fw-bold text-muted uppercase">Vessel Name</label>
                            <Input type="text" value={form?.vessel_name} onChange={e => setForm({ ...form, vessel_name: e?.target?.value })} />
                        </Col>
                        <Col md={2}>
                            <label className="small fw-bold text-muted uppercase">GST (%)</label>
                            <Input type="number" value={form?.gst_percent} onChange={e => setForm({ ...form, gst_percent: e?.target?.value })} />
                        </Col>
                    </Row>

                    {items?.map((item, i) => (
                        <Card key={i} className={`mb-4 border-0 rounded-4 shadow-sm border-start border-5 ${item.is_holiday ? 'bg-light-success border-success' : 'bg-light border-primary'}`}>
                            <CardBody>
                                <Row className="g-2 mb-3">
                                    <Col md={3}>
                                        <label className="extra-small fw-bold">Vehicle</label>
                                        <Select options={vehicles} value={item?.vehicle_id} onChange={v => handleItemChange(i, "vehicle_id", v)} />
                                    </Col>
                                    <Col md={3}>
                                        <label className="extra-small fw-bold">Driver</label>
                                        <Select options={drivers} value={item?.driver_id} onChange={v => handleItemChange(i, "driver_id", v)} />
                                    </Col>
                                    <Col md={2}><label className="extra-small fw-bold">Pallet</label><Input type="text" value={item?.pallet_per_lorry} onChange={e => handleItemChange(i, "pallet_per_lorry", e?.target?.value)} /></Col>
                                    <Col md={2}><label className="extra-small fw-bold">Pickup</label><Input type="text" value={item?.pickup_location} onChange={e => handleItemChange(i, "pickup_location", e?.target?.value)} /></Col>
                                    <Col md={2}><label className="extra-small fw-bold">Delivery</label><Input type="text" value={item?.delivery_location} onChange={e => handleItemChange(i, "delivery_location", e?.target?.value)} /></Col>
                                </Row>

                                <Row className="g-2 align-items-center">
                                    <Col md={2}>
                                        <label className="extra-small fw-bold">Trip Start</label>
                                        <Flatpickr className="form-control bg-white" value={item?.work_start_datetime} options={{ enableTime: true, dateFormat: "Y-m-d H:i" }} onChange={([d]) => handleItemChange(i, "work_start_datetime", d)} />
                                    </Col>
                                    <Col md={2}>
                                        <label className="extra-small fw-bold">Trip End</label>
                                        <Flatpickr className="form-control bg-white" value={item?.work_end_datetime} options={{ enableTime: true, dateFormat: "Y-m-d H:i" }} onChange={([d]) => handleItemChange(i, "work_end_datetime", d)} />
                                    </Col>
                                    <Col md={2}>
                                        <label className="extra-small fw-bold">Job Date</label>
                                        <DatePicker value={item?.job_date} onChange={v => handleItemChange(i, "job_date", v)} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                    </Col>
                                    {/* ⭐ Extra Charges Description ⭐ */}
                                    <Col md={3}>
                                        <label className="extra-small fw-bold text-info">Extra Charges Desc</label>
                                        <Input type="text" placeholder="Description" value={item?.extra_charges_description} onChange={e => handleItemChange(i, "extra_charges_description", e.target.value)} />
                                    </Col>
                                    {/* ⭐ Extra Charges Amount ⭐ */}
                                    <Col md={1}>
                                        <label className="extra-small fw-bold text-info">Extra Amt</label>
                                        <Input type="number" value={item?.extra_charges} onChange={e => handleItemChange(i, "extra_charges", e.target.value)} />
                                    </Col>
                                    <Col md={2} className="text-end">
                                        <label className="extra-small fw-bold text-primary small">Line Amount</label>
                                        <div className="h4 fw-bold text-dark mb-0">${item?.line_total}</div>
                                    </Col>
                                </Row>

                                <div className="mt-3 d-flex align-items-center gap-2 border-top pt-2">
                                    <Input type="checkbox" id={`holiday-${i}`} className="m-0" checked={item.is_holiday} onChange={e => handleItemChange(i, "is_holiday", e.target.checked)} />
                                    <Label for={`holiday-${i}`} className="m-0 small fw-bold text-danger cursor-pointer">Is this a public holiday trip?</Label>
                                </div>

                                {Number(item?.line_total || 0) > 0 && (
                                    <div className="mt-2 p-2 bg-white rounded border d-flex gap-3 overflow-auto small shadow-sm">
                                        <div className="text-nowrap text-muted">Normal: <b>{item?.normal_hrs}h</b> / OT: <b>{item?.normal_hrs_ot}h</b></div>
                                        <div className="text-nowrap text-muted">After Offz: <b>{item?.after_office_hrs}h</b> / OT: <b>{item?.offz_hrs_ot}h</b></div>
                                        <div className="text-nowrap text-success fw-bold">Holiday: <b>{item?.holiday}h</b> / OT: <b>{item?.holiday_ot}h</b></div>
                                        {Number(item?.extra_charges) > 0 && <div className="text-nowrap text-info fw-bold">Extra: <b>${item?.extra_charges}</b></div>}
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    ))}

                    <div className="d-flex justify-content-between align-items-center mt-5 border-top pt-4">
                        <Button color="dark" outline className="rounded-pill px-4" onClick={() => setItems([...items, { vehicle_id: null, driver_id: null, job_date: dayjs(), line_total: "0.00", quantity: 1, is_holiday: false, extra_charges: 0, extra_charges_description: "" }])}>
                            <i className="bi bi-plus-lg me-1"></i> Add Trip Line
                        </Button>
                        <div className="text-end">
                            <div className="h2 fw-bold text-success mb-3">Grand Total: ${grandTotal?.toFixed(2)}</div>
                            <Button color="primary" size="lg" className="px-5 rounded-pill shadow-lg shadow-primary" type="submit" disabled={loading}>
                                {loading ? "Updating..." : "Update Invoice"}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
            <style>{`
                .bg-light-success { background-color: #f0fdf4 !important; }
                .cursor-pointer { cursor: pointer; }
            `}</style>
        </LocalizationProvider>
    );
};

export default EditManualInvoice;